/* Showcase War Room — a faithful replica of the real /war-room screen for the public
   vault entrance. Same layout, same fonts, same orange, but every job, truck and number
   here is invented and generated client-side: no auth, no API, no Maps billing, and none
   of a real tenant's customers, prices or truck positions ever leave the building. */
import { useEffect, useRef, useState } from "react";
import ShowcaseMap from "@/components/ShowcaseMap";
import { CITY, SITES } from "@/lib/showcaseCity";
import { vol, mixLabel, IMP } from "@/lib/showcaseUnits";
import { Radar, Package, DollarSign, Radio, Plus, Printer, Truck as TruckIcon, Clock, ChevronRight, ShieldOff, AlertTriangle, Eye, HardHat, Zap, Check } from "lucide-react";

const ORANGE = "#F57C00";

const JOBS = [
  { id: "j1", time: "06:00", status: "completed", cust: "Riverstone Constructions", site: `14 Shearwater Dr, ${SITES[0].name}`, m3: 24, mix: "N32-20", mpa: 32, agg: 20, trucks: 3, delivered: 1, sent: 3, total: 3 },
  { id: "j2", time: "07:30", status: "in progress", cust: "Hargraves Civil", site: `Lot 22 Ocean Pde, ${SITES[1].name}`, m3: 18, mix: "N25-14", mpa: 25, agg: 14, trucks: 2, delivered: 0.61, sent: 2, total: 3, live: { truck: "SP-11", weighed: 5, materials: 8 } },
  { id: "j3", time: "08:15", status: "in progress", cust: "Coastline Homes", site: `9 Mercury St, ${SITES[2].name}`, m3: 13.5, mix: "N40-PUMP", mpa: 40, agg: 20, trucks: 2, delivered: 0.33, sent: 1, total: 2 },
  { id: "j4", time: "09:00", status: "scheduled", cust: "Bellambi Landscapes", site: `3 Quarry Rd, ${CITY.plantSuburb}`, m3: 7, mix: "N20-EXP", mpa: 20, agg: 10, trucks: 1, delivered: 0, sent: 0, total: 1 },
  { id: "j5", time: "10:30", status: "scheduled", cust: "Ellison Bros Formwork", site: `88 Industrial Ave, ${SITES[3].name}`, m3: 32, mix: "N32-FIB", mpa: 32, agg: 20, trucks: 3, delivered: 0, sent: 0, total: 4 },
  { id: "j6", time: "12:00", status: "scheduled", cust: "Marrickville Slabs", site: `41 Sunset Cr, ${SITES[4].name}`, m3: 9.5, mix: "N25-20", mpa: 25, agg: 20, trucks: 1, delivered: 0, sent: 0, total: 2 },
  { id: "j7", time: "14:00", status: "scheduled", cust: "Southpoint Developments", site: `Stage 4, ${SITES[1].name} Rise Estate`, m3: 46, mix: "N32-20", mpa: 32, agg: 20, trucks: 4, delivered: 0, sent: 0, total: 6 },
];

const TRUCKS = [
  { no: "SP-07", status: "en route", driver: "M. Dwyer", cap: 8, kph: 62, mix: "N32-20", mpa: 32, agg: 20, cust: "Riverstone Constructions", eta: "14 min", ret: "08:42", conf: "high", tint: ORANGE },
  { no: "SP-11", status: "loading", driver: "J. Petrie", cap: 7, kph: 0, mix: "N25-14", mpa: 25, agg: 14, cust: "Hargraves Civil", eta: "load 5/8", ret: "08:58", conf: "high", tint: "#3B82F6" },
  { no: "SP-03", status: "on site", driver: "T. Whelan", cap: 6, kph: 0, mix: "N40-PUMP", mpa: 40, agg: 20, cust: "Coastline Homes", eta: "6 min", ret: "09:10", conf: "medium", tint: "#22C55E" },
  { no: "SP-14", status: "en route", driver: "K. Rasmussen", cap: 8, kph: 54, mix: "N32-FIB", mpa: 32, agg: 20, cust: "Ellison Bros Formwork", eta: "22 min", ret: "09:26", conf: "medium", tint: ORANGE },
  { no: "SP-09", status: "returning", driver: "B. Halloran", cap: 7, kph: 71, ret: "08:35", conf: "high", tint: "#71717A" },
  { no: "SP-05", status: "lockout", driver: "no driver", cap: 6, kph: null, lockout: true, tint: "#EF4444" },
];



const money = (n) => `$${Math.round(n).toLocaleString()}`;

export default function ShowcaseWarRoom() {
  const [t, setT] = useState(0);
  const start = useRef(Date.now());
  useEffect(() => {
    const i = setInterval(() => setT((Date.now() - start.current) / 1000), 90);
    return () => clearInterval(i);
  }, []);


  const volume = 148.5 + Math.floor(t / 7) * 0.5;
  const revenue = 41380 + Math.floor(t / 7) * 137;
  const today = new Date().toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short" }).toUpperCase();

  return (
    <div className="absolute inset-0 flex flex-col bg-[#0B0C0E] text-zinc-200 overflow-hidden select-none" data-testid="showcase-war-room">
      {/* ── KPI strip ─────────────────────────────────────────────── */}
      <div className="border-b border-zinc-800 bg-[#121417]/95 backdrop-blur px-4 py-3 flex flex-wrap items-center gap-x-5 gap-y-2 pr-[11rem] lg:pr-[25rem]">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center" style={{ background: ORANGE }}>
            <Radar className="w-4 h-4 text-black" strokeWidth={2.5} />
          </div>
          <div>
            <div className="mono text-[9px] uppercase tracking-[0.28em] text-zinc-500">5 AM · Command</div>
            <div className="heading text-sm font-black !text-white">WAR ROOM</div>
          </div>
          <span className="ml-2 mono text-[9px] uppercase tracking-widest px-2 py-1 border border-zinc-700 text-zinc-500 hidden xl:inline">Classic Dispatch</span>
          <span className="ml-1 inline-flex items-center gap-1 mono text-[9px] uppercase tracking-widest font-black px-2 py-1 text-black" style={{ background: ORANGE }}>
            <Plus className="w-3 h-3" strokeWidth={3} /> New Job
          </span>
          <span className="ml-1 hidden xl:inline-flex items-center gap-1 mono text-[9px] uppercase tracking-widest px-2 py-1 border border-zinc-700 text-zinc-500">
            <Printer className="w-3 h-3" /> Print Schedule
          </span>
        </div>
        <div className="h-8 w-px bg-zinc-800 hidden sm:block" />
        <div className="flex items-center gap-2">
          <Package className="w-4 h-4" style={{ color: ORANGE }} />
          <div>
            <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">Today's Volume</div>
            <div className="heading text-xl font-black leading-none !text-white tabular-nums">
              {vol(volume).v.toFixed(1)}<span className="text-xs text-zinc-500 ml-1">{vol(volume).u}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-emerald-500" />
          <div>
            <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">Today's Revenue</div>
            <div className="heading text-xl font-black leading-none !text-emerald-500 tabular-nums">{money(revenue)}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 ml-auto text-xs text-zinc-500 mono uppercase tracking-widest">
          <Radio className="w-3.5 h-3.5 text-emerald-500 animate-pulse" />
          5 live · {TRUCKS.length} fleet · {JOBS.length} jobs
          <span className="ml-2 mono text-[10px]">{today}</span>
        </div>
      </div>

      {/* ── 3 columns ─────────────────────────────────────────────── */}
      <div className="flex-1 min-h-0 w-full flex flex-col lg:flex-row flex-nowrap gap-3 p-3">
        {/* LEFT · jobs */}
        <div className="border border-zinc-800 bg-[#101215] flex flex-col overflow-hidden lg:basis-1/3 w-full lg:w-1/3 min-w-0 lg:shrink-0 lg:grow-0">
          <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
            <div className="mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Jobs · {today}</div>
            <span className="mono text-[10px] uppercase tracking-widest" style={{ color: ORANGE }}>{JOBS.length}</span>
          </div>
          <div className="flex-1 overflow-hidden divide-y divide-zinc-800">
            {JOBS.map((j) => {
              const running = j.status === "in progress";
              const dPct = Math.round(j.delivered * 100);
              return (
                <div key={j.id} className={`px-3 py-2.5 border-l-2 ${running ? "bg-white/[0.03]" : "border-transparent"} ${j.status === "completed" ? "opacity-55" : ""}`}
                  style={running ? { borderLeftColor: ORANGE } : undefined}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="mono text-[10px] uppercase tracking-widest" style={{ color: ORANGE }}>{j.time}</span>
                    <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-zinc-700 text-zinc-500">{j.status}</span>
                  </div>
                  <div className="font-bold text-sm truncate mt-1 text-zinc-100">{j.cust}</div>
                  <div className="text-[11px] text-zinc-500 truncate">{j.site}</div>
                  <div className="flex items-center gap-2 mt-1 text-[11px] text-zinc-500">
                    <span className="mono text-zinc-200 font-bold">{vol(j.m3).v.toFixed(1)} {vol(j.m3).u}</span>
                    <span className="mono">·</span><span className="mono">{mixLabel(j.mix, j.mpa, j.agg)}</span>
                    <span className="ml-auto flex items-center gap-1"><TruckIcon className="w-3 h-3" />{j.trucks}</span>
                  </div>
                  <div className="mt-2 flex items-center gap-1">
                    <span className="mono text-[9px] uppercase tracking-widest text-zinc-500 mr-1">View as</span>
                    <span className="inline-flex items-center gap-1 mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-zinc-700 text-zinc-500"><Eye className="w-3 h-3" /> Customer</span>
                    <span className="inline-flex items-center gap-1 mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-zinc-700 text-zinc-500"><HardHat className="w-3 h-3" /> Pump</span>
                    <span className="ml-auto inline-flex items-center gap-1">
                      {j.live ? (
                        <span className="inline-flex items-center gap-1 mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-amber-500 text-amber-300">
                          Weighing {j.live.truck} · {j.live.weighed}/{j.live.materials}
                        </span>
                      ) : j.sent > 0 && (
                        <span className={`inline-flex items-center gap-1 mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border ${j.sent >= j.total ? "border-emerald-500 text-emerald-400" : "border-blue-500 text-blue-300"}`}>
                          <Check className="w-3 h-3" /> Sent {j.sent}/{j.total} loads
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1 mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-blue-600 border border-blue-500 text-white">
                        <Zap className="w-3 h-3" /> Batch
                      </span>
                    </span>
                  </div>
                  <div className="mt-2 h-2 border border-zinc-700 bg-zinc-900 flex overflow-hidden">
                    <div className="h-full transition-all" style={{ width: `${dPct}%`, background: ORANGE }} />
                    <div className="h-full transition-all" style={{ width: `${running ? 14 : 0}%`, background: "rgba(245,124,0,.35)" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* MIDDLE · fleet + safety */}
        <div className="border border-zinc-800 bg-[#101215] flex flex-col overflow-hidden lg:basis-1/3 w-full lg:w-1/3 min-w-0 lg:shrink-0 lg:grow-0">
          <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
            <div className="mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Fleet · Safety</div>
            <span className="mono text-[10px] uppercase tracking-widest text-red-400">1 lockout</span>
          </div>
          <div className="mx-3 mt-3 border border-red-600 bg-red-950/40 text-red-200 p-2 text-[11px] flex items-start gap-2">
            <ShieldOff className="w-4 h-4 mt-0.5 shrink-0 animate-pulse text-red-400" />
            <div>
              <div className="mono uppercase tracking-widest text-red-300 text-[10px]">Safety Lockout Active</div>
              <div className="mt-0.5">1 truck missing today's pre-start. Dispatch is blocked until submitted or manually overridden.</div>
            </div>
          </div>
          <div className="flex-1 overflow-hidden p-2 space-y-1.5">
            {TRUCKS.map((tk) => (
              <div key={tk.no} className={`w-full text-left border-2 bg-white/[0.02] px-2.5 py-2 flex items-center gap-2 ${tk.lockout ? "border-red-500" : "border-zinc-800"}`}>
                <TruckIcon className="w-4 h-4 shrink-0" style={{ color: tk.tint }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className={`mono text-sm font-bold ${tk.lockout ? "text-red-400" : "text-zinc-100"}`}>{tk.no}</span>
                    <span className="mono text-[8px] uppercase tracking-widest px-1 py-0.5 border" style={{ borderColor: tk.tint, color: tk.tint }}>{tk.status}</span>
                  </div>
                  <div className="text-[11px] text-zinc-500 truncate flex items-center gap-1">
                    <span className="truncate">{tk.driver} · {vol(tk.cap).v.toFixed(1)} {vol(tk.cap).u}{tk.kph ? ` · ${IMP ? Math.round(tk.kph * 0.621371) + " mph" : tk.kph + " km/h"}` : ""}</span>
                    {!tk.mix && tk.ret && (
                      <span className="mono uppercase tracking-widest ml-auto px-1 py-0.5 border border-emerald-600/60 text-emerald-400 text-[9px] shrink-0">↺ {tk.ret}</span>
                    )}
                  </div>
                  {tk.mix && (
                    <div className="flex items-center gap-1.5 mt-0.5 text-[10px]">
                      <span className="truncate" style={{ color: ORANGE }}>{mixLabel(tk.mix, tk.mpa, tk.agg)} → {tk.cust}</span>
                      <span className="mono uppercase tracking-widest ml-auto px-1 py-0.5 border border-zinc-700 shrink-0" style={{ color: ORANGE }}>
                        <Clock className="w-2.5 h-2.5 inline mr-0.5" />{tk.eta}
                      </span>
                      <span className={`mono uppercase tracking-widest px-1 py-0.5 border shrink-0 ${tk.conf === "high" ? "border-emerald-500 text-emerald-400" : "border-sky-500 text-sky-400"}`}>↺ {tk.ret}</span>
                    </div>
                  )}
                </div>
                {tk.lockout && <AlertTriangle className="w-4 h-4 text-red-400 animate-pulse" />}
                <ChevronRight className="w-3.5 h-3.5 text-zinc-600" />
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT · live map */}
        <div className="border border-zinc-800 bg-[#EDE9E3] overflow-hidden relative z-0 basis-1/3 w-1/3 min-w-0 shrink-0 grow-0 hidden lg:block">
          <div className="absolute top-2 left-2 z-[600] flex bg-[#0B0C0E] border border-zinc-800">
            {["Normal", "Dark", "Satellite"].map((v, i) => (
              <span key={v} className={`mono text-[9px] uppercase tracking-widest px-2 py-1 ${i === 0 ? "text-black font-black" : "text-zinc-500"}`}
                style={i === 0 ? { background: ORANGE } : undefined}>{v}</span>
            ))}
          </div>
          <div className="absolute top-2 right-2 z-[600] mono text-[9px] uppercase tracking-widest text-zinc-500 bg-[#0B0C0E] border border-zinc-800 px-2 py-1">
            Live GPS · 5 tracking
          </div>
          <ShowcaseMap />
          <div className="absolute top-10 left-2 z-[600] mono text-[8px] tracking-wide text-zinc-700 bg-white/85 px-1.5 py-0.5">
            © OpenStreetMap contributors
          </div>
          {/* selected-truck info card, as on the real board */}
          <div className="absolute bottom-2 left-2 right-2 z-[600] border border-zinc-800 bg-[#101215]/95 backdrop-blur p-2.5">
            <div className="flex items-center gap-2">
              <TruckIcon className="w-4 h-4" style={{ color: ORANGE }} />
              <div className="heading font-black text-base leading-none !text-white">SP-07</div>
              <span className="mono text-[8px] uppercase tracking-widest px-1 py-0.5 border" style={{ borderColor: ORANGE, color: ORANGE }}>en route</span>
              <span className="ml-auto mono text-[9px] uppercase tracking-widest text-zinc-500">{IMP ? "39 mph" : "62 km/h"}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs mt-2">
              <div><div className="mono text-[9px] uppercase tracking-widest text-zinc-500">Mix</div><div className="font-bold text-zinc-100">{mixLabel("N32-20", 32, 20)}{IMP ? "" : " · 32 MPa"}</div></div>
              <div><div className="mono text-[9px] uppercase tracking-widest text-zinc-500">Volume</div><div className="font-bold text-zinc-100">{vol(7.5).v.toFixed(1)} {vol(7.5).u}</div></div>
              <div className="col-span-2"><div className="mono text-[9px] uppercase tracking-widest text-zinc-500">Destination</div><div className="text-xs truncate text-zinc-300">14 Shearwater Dr, {SITES[0].name}</div></div>
              <div><div className="mono text-[9px] uppercase tracking-widest text-zinc-500">ETA</div><div className="font-bold flex items-center gap-1" style={{ color: ORANGE }}><Clock className="w-3 h-3" />14 min</div></div>
              <div><div className="mono text-[9px] uppercase tracking-widest text-zinc-500">Predicted return</div><div className="font-bold text-emerald-400">↺ 08:42</div></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
